<script>
  import { onMount } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import Pagination from '$lib/Pagination.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import {
    faRotate,
    faPlus,
    faPencil,
    faTrash,
    faSquarePlus,
    faTrashCan,
    faHistory,
    faXmark,
    faSortUp,
    faSortDown,
    faCheck,
    faWandMagicSparkles,
    faCircleXmark,
  } from '@fortawesome/pro-light-svg-icons';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { user, userRoles, userReadOnly } from '$stores/sessionStore';
  import { studyProperties } from '$stores/configStore';
  import { get } from 'svelte/store';
  import { hasAccessUiAction } from '$utils/permissionUtils.js';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import StudyDetails from '$lib/StudyDetails.svelte';
  import ConfirmationModal from '$lib/ConfirmationModal.svelte';
  import AuditLogModal from '$lib/AuditLogModal.svelte';
  import LocalizationEntryModal from '$lib/LocalizationEntryModal.svelte';
  import pkg from 'file-saver';
  import { exportToExcel } from '$utils/export.js';
  const { saveAs } = pkg;

  // Use local API endpoints instead of direct API with key
  const API_BASE_URL = '/api/proxy';

  let entries = [];
  let resultCount = 0;
  let currentPage = 1;
  let totalPages = 1;
  let limit = 20;
  let searchText = '';
  let sortField = 'key';
  let sortOrder = 'asc';
  let selectedType = 'String';
  let selectedPlatform = 'Web';
  let selectedLanguage = null;
  let languages = [];
  let translations = null;

  // Available options for filters
  const typeOptions = ['String', 'Message'];
  const platformOptions = ['iOS', 'Android', 'Web', 'Messaging'];

  // Modal state
  let showCreateModal = false;
  let showEditModal = false;
  let showDeleteModal = false;
  let showAuditLog = false;
  let selectedEntry = null;
  let auditLogEntry = null;
  let isLoading = false;
  let showTranslationModal = false;
  let translationModalEntry = null;
  let translationModalType = null; // 'create' or 'edit'
  let translationModalContent = {};
  let translationModalId = null;
  let editingTranslation = null;
  let editingContent = null;
  let isAIGenerating = false;

  // Normalize MongoDB ObjectId-like values to a string id
  function toIdString(id) {
    if (!id) return '';
    if (typeof id === 'string') return id;
    if (id.$oid) return id.$oid;
    if (typeof id.toString === 'function') return id.toString();
    try { return String(id); } catch { return ''; }
  }

  // Computed platform options based on selected type
  $: availablePlatformOptions =
    selectedType === 'String' ? ['iOS', 'Android', 'Web'] : ['Messaging'];

  // Update platform selection when type changes
  $: {
    if (selectedType === 'Message') {
      selectedPlatform = 'Messaging';
    } else if (selectedType === 'String' && selectedPlatform === 'Messaging') {
      selectedPlatform = 'Web';
    }
  }

  // Add progress tracking variables
  let importProgress = {
    total: 0,
    processed: 0,
    created: 0,
    updated: 0,
    skipped: 0,
    failed: 0
  };
  let showImportProgress = false;

  onMount(async () => {
    selectedSite.set(null);
    selectedParticipant.set(null);
    await fetchLanguages();
    // Do not call fetchEntries here
    window.addEventListener('keydown', handleKeydown);
  });

  onMount(() => {
    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  });

  async function fetchLanguages() {
    try {
      const res = await fetch(`${API_BASE_URL}/languages?pageSize=999`);
      if (!res.ok) {
        throw new Error('Failed to fetch languages');
      }
      const data = await res.json();
      languages = data.data || [];
    } catch (err) {
      console.error('Error fetching languages:', err);
      languages = [];
    }
  }

  // Set default language when languages are loaded
  $: if (languages.length > 0 && !selectedLanguage) {
    const englishLanguage = languages.find((lang) => lang.code === 'en_US');
    selectedLanguage = englishLanguage ? englishLanguage.code : languages[0].code;
  }

  // Fetch entries whenever selectedLanguage changes and languages are loaded
  $: if (selectedLanguage && languages.length > 0) {
    fetchEntries(1);
  }

  function handleLanguageChange(event) {
    selectedLanguage = event.target.value;
  }

  async function fetchEntries(page = 1) {
    try {
      isLoading = true;
      translations = null; // Set to null to indicate loading

      let url = `${API_BASE_URL}/localization?page=${page}&pageSize=${limit}&sortField=${sortField}&sortOrder=${sortOrder}`;

      if (searchText) {
        url += `&searchText=${encodeURIComponent(searchText)}`;
      }
      if (selectedType) {
        url += `&type=${encodeURIComponent(selectedType)}`;
      }
      if (selectedPlatform) {
        url += `&platform=${encodeURIComponent(selectedPlatform)}`;
      }
      if (selectedLanguage) {
        url += `&language=${encodeURIComponent(selectedLanguage)}`;
      }

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch localization entries');
      }

      const responseData = await res.json();

      resultCount = responseData.totalCount || 0;
      currentPage = responseData.page || 1;
      totalPages = responseData.totalPages || 1;
      entries = responseData.data;

      // Fetch all translations for the selected language
      if (selectedLanguage) {
        const tRes = await fetch(
          `${API_BASE_URL}/translations?languageCode=${selectedLanguage}`
        );
        if (tRes.ok) {
          const tData = await tRes.json();
          translations = tData.data || [];
        } else {
          translations = [];
        }
      } else {
        translations = [];
      }
    } catch (err) {
      console.error('Error fetching localization entries:', err);
      entries = [];
      translations = [];
    } finally {
      isLoading = false;
    }
  }

  function handleRefresh() {
    fetchEntries(currentPage);
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      fetchEntries(newPage);
    }
  }

  function handleSort(field) {
    if (sortField === field) {
      sortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      sortField = field;
      sortOrder = 'asc';
    }
    fetchEntries(1);
  }

  function handleClearSearch() {
    searchText = '';
    fetchEntries(1);
  }

  function openCreateModal() {
    selectedEntry = {
      key: '',
      ruleId: null,
      type: 'String',
      platforms: [],
      placeholders: [],
      defaultLanguageCode: 'en_US',
      defaultContent: {
        value: '',
      },
    };
    showCreateModal = true;
  }

  function openEditModal(entry) {
    selectedEntry = { ...entry };
    showEditModal = true;
  }

  function openDeleteModal(entry) {
    selectedEntry = entry;
    showDeleteModal = true;
  }

  function handleAuditLogClick(entry) {
    auditLogEntry = entry;
    showAuditLog = true;
  }

  async function handleCreateEntry(entry) {
    try {
      // Check permission before creating
      if (!hasAccessUiAction('localization.add', get(userRoles), get(studyProperties), get(userReadOnly))) {
        console.error('Insufficient permissions to create localization entry');
        return;
      }

      isLoading = true;
      const res = await fetch(`${API_BASE_URL}/localization`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(entry),
      });

      if (!res.ok) {
        throw new Error('Failed to create localization entry');
      }

      showCreateModal = false;
      await fetchEntries(1);
    } catch (err) {
      console.error('Error creating localization entry:', err);
    } finally {
      isLoading = false;
    }
  }

  async function handleUpdateEntry(entry) {
    try {
      // Check permission before updating
      if (!hasAccessUiAction('localization.edit', get(userRoles), get(studyProperties), get(userReadOnly))) {
        console.error('Insufficient permissions to update localization entry');
        return;
      }

      isLoading = true;
      const res = await fetch(`${API_BASE_URL}/localization/${entry._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(entry),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to update localization entry');
      }

      showEditModal = false;
      await fetchEntries(currentPage);
    } catch (err) {
      console.error('Error updating localization entry:', err);
      alert(err.message || 'Failed to update localization entry');
    } finally {
      isLoading = false;
    }
  }

  async function handleDeleteEntry() {
    try {
      // Check permission before deleting
      if (!hasAccessUiAction('localization.delete', get(userRoles), get(studyProperties), get(userReadOnly))) {
        console.error('Insufficient permissions to delete localization entry');
        return;
      }

      isLoading = true;
      const res = await fetch(`${API_BASE_URL}/localization/${selectedEntry._id}`, {
        method: 'DELETE',
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to delete localization entry');
      }

      showDeleteModal = false;
      await fetchEntries(currentPage);
    } catch (err) {
      console.error('Error deleting localization entry:', err);
      alert(err.message || 'Failed to delete localization entry');
    } finally {
      isLoading = false;
    }
  }

  // Add computed property for edit permissions
  $: canAddEntries = hasAccessUiAction('localization.add', $userRoles, $studyProperties, $userReadOnly);
  $: canEditEntries = hasAccessUiAction('localization.edit', $userRoles, $studyProperties, $userReadOnly);
  $: canDeleteEntries = hasAccessUiAction('localization.delete', $userRoles, $studyProperties, $userReadOnly);

  // Helper function to get content preview
  function getContentPreview(entry) {
    if (entry.type === 'String') {
      return entry.defaultContent.value;
    } else if (entry.type === 'Message') {
      return `${entry.defaultContent.title}: ${entry.defaultContent.message}`;
    }
    return '';
  }

  //   // Debug: log entry and translation IDs
  //   $: if (entries.length && translations.length) {
  //     console.log('DEBUG: entries', entries.map(e => e._id));
  //     console.log('DEBUG: translations', translations.map(t => t.entryId));
  //   }

  function getTranslationForEntry(entryId) {
    const entryIdStr = typeof entryId === 'string' ? entryId : entryId?.$oid || entryId?.toString();
    return translations.find((t) => {
      const tId =
        typeof t.entryId === 'string' ? t.entryId : t.entryId?.$oid || t.entryId?.toString();
      return tId === entryIdStr;
    });
  }

  // Add computed loading state
  $: isTableLoading = isLoading || translations === null;

  function openCreateTranslationModal(entry) {
    translationModalEntry = entry;
    translationModalType = 'create';
    translationModalContent = entry.type === 'String' ? { value: '' } : { title: '', message: '' };
    showTranslationModal = true;
  }

  function openEditTranslationModal(entry) {
    const translation = getTranslationForEntry(entry._id);
    if (!translation) return;
    translationModalEntry = entry;
    translationModalType = 'edit';
    translationModalContent = { ...translation.content };
    translationModalId = translation._id;
    showTranslationModal = true;
  }

  async function handleSaveTranslation() {
    if (!translationModalEntry) return;
    const payload = {
      entryId: toIdString(translationModalEntry._id),
      languageCode: selectedLanguage,
      content: translationModalContent,
      version: 1,
    };

    // Basic client-side validation for clearer errors
    if (!payload.entryId) {
      alert('Cannot save: missing entryId');
      return;
    }
    if (!payload.languageCode) {
      alert('Cannot save: missing languageCode');
      return;
    }
    try {
      let res;
      if (translationModalType === 'edit' && translationModalId) {
        res = await fetch(`/api/v1/translations/${translationModalId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch('/api/v1/translations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      if (!res.ok) {
        let msg = 'Failed to save translation';
        try {
          const err = await res.json();
          if (err?.message) msg = `${msg}: ${err.message}`;
        } catch {}
        throw new Error(msg);
      }
      showTranslationModal = false;
      await fetchEntries(currentPage);
    } catch (err) {
      alert(err.message || 'Failed to save translation');
    }
  }

  function startInlineEdit(entry) {
    const translation = getTranslationForEntry(entry._id);
    editingTranslation = entry;
    editingContent = translation
      ? { ...translation.content }
      : entry.type === 'String'
        ? { value: '' }
        : { title: '', message: '' };
  }

  function cancelInlineEdit() {
    editingTranslation = null;
    editingContent = null;
  }

  async function saveInlineEdit() {
    if (!editingTranslation) return;

    const payload = {
      entryId: toIdString(editingTranslation._id),
      languageCode: selectedLanguage,
      content: editingContent,
      version: 1,
    };

    if (!payload.entryId) {
      alert('Cannot save: missing entryId');
      return;
    }
    if (!payload.languageCode) {
      alert('Cannot save: missing languageCode');
      return;
    }

    try {
      const translation = getTranslationForEntry(editingTranslation._id);
      let res;

      if (translation) {
        res = await fetch(`/api/v1/translations/${translation._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch('/api/v1/translations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      if (!res.ok) {
        let msg = 'Failed to save translation';
        try {
          const err = await res.json();
          if (err?.message) msg = `${msg}: ${err.message}`;
        } catch {}
        throw new Error(msg);
      }
      await fetchEntries(currentPage);
      cancelInlineEdit();
    } catch (err) {
      alert(err.message || 'Failed to save translation');
    }
  }

  // Handle escape key to cancel editing
  function handleKeydown(event) {
    if (event.key === 'Escape' && editingTranslation) {
      cancelInlineEdit();
    }
  }

  async function generateAITranslation(entry, languageCode) {
    try {
      let content;
      if (entry.type === 'String') {
        content = entry.defaultContent.value;
      } else if (entry.type === 'Message') {
        content = `Title: ${entry.defaultContent.title}\nMessage: ${entry.defaultContent.message}`;
      } else {
        throw new Error('Unsupported entry type');
      }

      const response = await fetch('/api/v1/openai/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          messages: [
            {
              role: 'system',
              content: `You are a professional medical translator specializing in clinical trials and health-related applications.

Your task is to translate the following string from English (en_US) to ${languageCode}.

Requirements:
- Use terminology consistent with clinical trials and diabetes-related studies, especially Continuous Glucose Monitoring (CGM).
- Ensure translations are appropriate for user-facing mobile and web applications. The tone must be clear, concise, and natural to native users.
- Avoid literal translations if they sound unnatural or are inconsistent with domain standards in the target language.
- If the term is ambiguous or could have different meanings, choose the one that makes most sense in the context of a digital app supporting clinical trials.
- Keep the translated string in the same format, including placeholders or variables.
- Do not add interpunction if there is no interpunction in the original text.
- Do not return field names or other non-translation text.

String to translate: "${content}"`,
            },
            {
              role: 'user',
              content: content
            }
          ]
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate translation');
      }

      const data = await response.json();
      const translatedText = data.choices[0].message.content.trim();

      // Handle the response based on entry type
      if (entry.type === 'String') {
        editingContent.value = translatedText;
      } else if (entry.type === 'Message') {
        // Try to parse the response as title and message
        const lines = translatedText.split('\n');
        if (lines.length >= 2) {
          editingContent.title = lines[0].replace('Title:', '').trim();
          editingContent.message = lines[1].replace('Message:', '').trim();
        } else {
          // If we can't parse it properly, put the whole text in the message
          editingContent.message = translatedText;
        }
      }

      return translatedText;
    } catch (error) {
      console.error('Translation generation error:', error);
      throw new Error('Failed to generate translation');
    }
  }

  async function handleImportIOSStrings(event) {
    const file = event.target.files[0];
    if (!file) return;

    // Reset progress
    importProgress = {
      total: 0,
      processed: 0,
      created: 0,
      updated: 0,
      skipped: 0,
      failed: 0
    };
    showImportProgress = true;

    console.log('Starting import of iOS strings file:', file.name);
    
    try {
      const text = await file.text();
      console.log('File content loaded, length:', text.length);
      
      const lines = text.split('\n');
      const validLines = lines.filter(line => line.trim() && !line.trim().startsWith('//'));
      importProgress.total = validLines.length;
      console.log('Number of valid lines:', importProgress.total);
      
      let isDefaultLanguage = selectedLanguage === 'en_US';
      console.log('Importing for language:', selectedLanguage, 'isDefault:', isDefaultLanguage);

      // Fetch all iOS entries once
      const allEntriesRes = await fetch(`${API_BASE_URL}/localization?platform=iOS&type=String&pageSize=9999`);
      if (!allEntriesRes.ok) {
        const errorData = await allEntriesRes.json();
        throw new Error(`Failed to fetch all iOS entries. API Error: ${JSON.stringify(errorData)}`);
      }
      const allEntriesData = await allEntriesRes.json();
      const allEntries = allEntriesData.data || [];
      // Build a lookup map: key|platform -> entry
      const entryMap = {};
      for (const entry of allEntries) {
        for (const platform of entry.platforms) {
          entryMap[`${entry.key}|${platform}`] = entry;
        }
      }
      console.log('entryMap keys:', Object.keys(entryMap));

      // Fetch all translations for en_US
      const allTranslationsRes = await fetch(`${API_BASE_URL}/translations?languageCode=en_US`);
      if (!allTranslationsRes.ok) {
        const errorData = await allTranslationsRes.json();
        throw new Error(`Failed to fetch all en_US translations. API Error: ${JSON.stringify(errorData)}`);
      }
      const allTranslationsData = await allTranslationsRes.json();
      const allTranslations = allTranslationsData.data || [];
      // Build a lookup map: entryId|languageCode -> translation
      const translationMap = {};
      for (const t of allTranslations) {
        const id = typeof t.entryId === 'string' ? t.entryId : t.entryId?.$oid || t.entryId?.toString();
        translationMap[`${id}|${t.languageCode}`] = t;
      }
      console.log('translationMap keys:', Object.keys(translationMap));

      // Fetch all translations for the selected language if not default
      if (!isDefaultLanguage) {
        const selectedLangTranslationsRes = await fetch(`${API_BASE_URL}/translations?languageCode=${selectedLanguage}`);
        if (!selectedLangTranslationsRes.ok) {
          const errorData = await selectedLangTranslationsRes.json();
          throw new Error(`Failed to fetch all ${selectedLanguage} translations. API Error: ${JSON.stringify(errorData)}`);
        }
        const selectedLangTranslationsData = await selectedLangTranslationsRes.json();
        const selectedLangTranslations = selectedLangTranslationsData.data || [];
        // Add to the same lookup map
        for (const t of selectedLangTranslations) {
          const id = typeof t.entryId === 'string' ? t.entryId : t.entryId?.$oid || t.entryId?.toString();
          translationMap[`${id}|${t.languageCode}`] = t;
        }
      }

      for (const line of lines) {
        // Skip empty lines and comments
        if (!line.trim() || line.trim().startsWith('//')) continue;

        // Parse the line using regex
        const match = line.match(/"([^"]+)"\s*=\s*"([^"]+)";/);
        if (!match) continue;

        const [, key, value] = match;
        console.log('Processing entry:', { key, value });
        
        try {
          if (isDefaultLanguage) {
            // Use in-memory map to check for existing entry for iOS
            const entryKey = `${key}|iOS`;
            console.log('Looking for entryKey:', entryKey, 'in entryMap:', !!entryMap[entryKey]);
            const existingEntry = entryMap[entryKey];

            if (existingEntry) {
              const entryIdStr = typeof existingEntry._id === 'string' ? existingEntry._id : existingEntry._id?.$oid || existingEntry._id?.toString();
              const existingTranslation = translationMap[`${entryIdStr}|en_US`];
              let entryUpdated = false;
              if (existingEntry.defaultContent.value !== value) {
                console.log('Updating existing iOS entry:', key);
                const updatedEntry = {
                  ...existingEntry,
                  defaultContent: {
                    value
                  }
                };
                const res = await fetch(`${API_BASE_URL}/localization/${existingEntry._id}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(updatedEntry)
                });
                if (!res.ok) {
                  const errorData = await res.json();
                  throw new Error(`Failed to update entry for key: ${key}. API Error: ${JSON.stringify(errorData)}`);
                }
                console.log('Updated entry:', key);
                importProgress.updated++;
                entryUpdated = true;
                // Also update translation if it exists and value is different
                if (existingTranslation && existingTranslation.content.value !== value) {
                  const updateTranslationRes = await fetch(`${API_BASE_URL}/translations/${existingTranslation._id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      ...existingTranslation,
                      content: { value }
                    })
                  });
                  if (!updateTranslationRes.ok) {
                    const errorData = await updateTranslationRes.json();
                    throw new Error(`Failed to update translation for key: ${key}. API Error: ${JSON.stringify(errorData)}`);
                  }
                  console.log('Updated translation for:', key);
                  importProgress.updated++;
                }
              }
              if (!existingTranslation) {
                // Create new translation
                const newTranslation = {
                  entryId: entryIdStr,
                  languageCode: 'en_US',
                  content: { value },
                  version: 1
                };
                const createTranslationRes = await fetch(`${API_BASE_URL}/translations`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(newTranslation)
                });
                if (!createTranslationRes.ok) {
                  const errorData = await createTranslationRes.json();
                  throw new Error(`Failed to create translation for key: ${key}. API Error: ${JSON.stringify(errorData)}`);
                }
                console.log('Created translation for:', key);
                importProgress.created++;
                translationMap[`${entryIdStr}|en_US`] = newTranslation;
              }
              if (existingEntry.defaultContent.value === value && existingTranslation) {
                importProgress.skipped++;
                console.log('Skipping unchanged entry and translation:', key);
              }
            } else {
              // Create new entry for iOS platform
              console.log('Creating new iOS entry:', key);
              const newEntry = {
                key,
                type: 'String',
                platforms: ['iOS'],
                defaultLanguageCode: 'en_US',
                defaultContent: {
                  value
                }
              };
              const res = await fetch(`${API_BASE_URL}/localization`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newEntry)
              });
              if (!res.ok) {
                const errorData = await res.json();
                throw new Error(`Failed to create entry for key: ${key}. API Error: ${JSON.stringify(errorData)}`);
              }
              const createdEntryResponse = await res.json();
              // Use entryId or _id from response
              const entryId = createdEntryResponse.entryId || createdEntryResponse._id;
              console.log('Created entry:', createdEntryResponse);
              importProgress.created++;
              // Add to in-memory map so subsequent lines see it
              entryMap[entryKey] = { ...newEntry, _id: entryId, platforms: ['iOS'] };
              
              // In-memory translation check
              const entryIdStr = typeof entryId === 'string' ? entryId : entryId?.$oid || entryId?.toString();
              const existingTranslation = translationMap[`${entryIdStr}|en_US`];
              if (!existingTranslation) {
                // Create new translation
                const newTranslation = {
                  entryId: entryIdStr,
                  languageCode: 'en_US',
                  content: { value },
                  version: 1
                };
                const createTranslationRes = await fetch(`${API_BASE_URL}/translations`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(newTranslation)
                });
                if (!createTranslationRes.ok) {
                  const errorData = await createTranslationRes.json();
                  throw new Error(`Failed to create translation for key: ${key}. API Error: ${JSON.stringify(errorData)}`);
                }
                console.log('Created translation for:', key);
                importProgress.created++;
                // Add to in-memory map so subsequent lines see it
                translationMap[`${entryIdStr}|en_US`] = newTranslation;
              } else {
                console.log('Skipping unchanged entry and translation:', key);
                importProgress.skipped++;
              }
            }
          } else {
            // For non-default languages, use in-memory map
            const entryKey = `${key}|iOS`;
            const existingEntry = entryMap[entryKey];
            if (existingEntry) {
              const entryIdStr = typeof existingEntry._id === 'string' ? existingEntry._id : existingEntry._id?.$oid || existingEntry._id?.toString();
              const existingTranslation = translationMap[`${entryIdStr}|${selectedLanguage}`];

              if (existingTranslation) {
                // Update if content has changed
                if (existingTranslation.content.value !== value) {
                  console.log('Updating translation for iOS entry:', key);
                  const updateTranslationRes = await fetch(`${API_BASE_URL}/translations/${existingTranslation._id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      ...existingTranslation,
                      content: { value }
                    })
                  });
                  if (!updateTranslationRes.ok) {
                    const errorData = await updateTranslationRes.json();
                    throw new Error(`Failed to update translation for key: ${key}. API Error: ${JSON.stringify(errorData)}`);
                  }
                  console.log('Updated translation for:', key);
                  importProgress.updated++;
                } else {
                  console.log('Skipping unchanged translation:', key);
                  importProgress.skipped++;
                }
              } else {
                // Create new translation
                console.log('Creating new translation for iOS entry:', key);
                const translation = {
                  entryId: entryIdStr,
                  languageCode: selectedLanguage,
                  content: {
                    value
                  },
                  version: 1
                };
                const res = await fetch(`${API_BASE_URL}/translations`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(translation)
                });
                if (!res.ok) {
                  const errorData = await res.json();
                  throw new Error(`Failed to create translation for key: ${key}. API Error: ${JSON.stringify(errorData)}`);
                }
                console.log('Created new translation for:', key);
                importProgress.created++;
              }
            } else {
              console.warn('Skipping translation for non-existent iOS entry:', key);
              importProgress.skipped++;
            }
          }
          importProgress.processed++;
        } catch (error) {
          console.error(`Error processing key ${key}:`, error);
          importProgress.failed++;
        }
      }

      // Refresh the list
      await fetchEntries(currentPage);
      console.log('Import completed successfully');
    } catch (error) {
      console.error('Error importing file:', error);
      alert('Error importing file: ' + error.message);
    } finally {
      showImportProgress = false;
    }

    // Reset the file input
    event.target.value = '';
  }

  async function handleExportIOSStrings() {
    try {
      // Get all entries for iOS platform
      const res = await fetch(
        `${API_BASE_URL}/localization?platform=iOS&type=String&pageSize=999`
      );
      if (!res.ok) throw new Error('Failed to fetch entries');
      const data = await res.json();
      const entries = data.data || [];

      // Get translations for selected language
      const tRes = await fetch(
        `${API_BASE_URL}/translations?languageCode=${selectedLanguage}`
      );
      if (!tRes.ok) throw new Error('Failed to fetch translations');
      const tData = await tRes.json();
      const translations = tData.data || [];

      // Create the strings file content
      let content = '';
      for (const entry of entries) {
        const translation = translations.find(t => t.entryId === entry._id);
        const value = translation?.content?.value || entry.defaultContent.value;
        content += `"${entry.key}" = "${value}";\n`;
      }

      // Create and download the file
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      const language = languages.find(l => l.code === selectedLanguage);
      const fileName = `${language?.code || selectedLanguage}_Localizable.strings`;
      saveAs(blob, fileName);
    } catch (error) {
      console.error('Error exporting file:', error);
      alert('Error exporting file: ' + error.message);
    }
  }

  async function handleImportAndroidStrings(event) {
    const file = event.target.files[0];
    if (!file) return;

    // Reset progress
    importProgress = {
      total: 0,
      processed: 0,
      created: 0,
      updated: 0,
      skipped: 0,
      failed: 0
    };
    showImportProgress = true;

    console.log('Starting import of Android strings file:', file.name);
    
    try {
      const text = await file.text();
      console.log('File content loaded, length:', text.length);
      
      const jsonData = JSON.parse(text);
      const entries = Object.entries(jsonData);
      importProgress.total = entries.length;
      console.log('Number of entries:', importProgress.total);
      
      let isDefaultLanguage = selectedLanguage === 'en_US';
      console.log('Importing for language:', selectedLanguage, 'isDefault:', isDefaultLanguage);

      // Fetch all Android entries once
      const allEntriesRes = await fetch(`${API_BASE_URL}/localization?platform=Android&type=String&pageSize=9999`);
      if (!allEntriesRes.ok) {
        const errorData = await allEntriesRes.json();
        throw new Error(`Failed to fetch all Android entries. API Error: ${JSON.stringify(errorData)}`);
      }
      const allEntriesData = await allEntriesRes.json();
      const allEntries = allEntriesData.data || [];
      // Build a lookup map: key|platform -> entry
      const entryMap = {};
      for (const entry of allEntries) {
        for (const platform of entry.platforms) {
          entryMap[`${entry.key}|${platform}`] = entry;
        }
      }
      console.log('entryMap keys:', Object.keys(entryMap));

      // Fetch all translations for en_US
      const allTranslationsRes = await fetch(`${API_BASE_URL}/translations?languageCode=en_US`);
      if (!allTranslationsRes.ok) {
        const errorData = await allTranslationsRes.json();
        throw new Error(`Failed to fetch all en_US translations. API Error: ${JSON.stringify(errorData)}`);
      }
      const allTranslationsData = await allTranslationsRes.json();
      const allTranslations = allTranslationsData.data || [];
      // Build a lookup map: entryId|languageCode -> translation
      const translationMap = {};
      for (const t of allTranslations) {
        const id = typeof t.entryId === 'string' ? t.entryId : t.entryId?.$oid || t.entryId?.toString();
        translationMap[`${id}|${t.languageCode}`] = t;
      }
      console.log('translationMap keys:', Object.keys(translationMap));

      // Fetch all translations for the selected language if not default
      if (!isDefaultLanguage) {
        const selectedLangTranslationsRes = await fetch(`${API_BASE_URL}/translations?languageCode=${selectedLanguage}`);
        if (!selectedLangTranslationsRes.ok) {
          const errorData = await selectedLangTranslationsRes.json();
          throw new Error(`Failed to fetch all ${selectedLanguage} translations. API Error: ${JSON.stringify(errorData)}`);
        }
        const selectedLangTranslationsData = await selectedLangTranslationsRes.json();
        const selectedLangTranslations = selectedLangTranslationsData.data || [];
        // Add to the same lookup map
        for (const t of selectedLangTranslations) {
          const id = typeof t.entryId === 'string' ? t.entryId : t.entryId?.$oid || t.entryId?.toString();
          translationMap[`${id}|${t.languageCode}`] = t;
        }
      }

      for (const [key, value] of entries) {
        console.log('Processing entry:', { key, value });
        
        try {
          if (isDefaultLanguage) {
            // Use in-memory map to check for existing entry for Android
            const entryKey = `${key}|Android`;
            console.log('Looking for entryKey:', entryKey, 'in entryMap:', !!entryMap[entryKey]);
            const existingEntry = entryMap[entryKey];

            if (existingEntry) {
              const entryIdStr = typeof existingEntry._id === 'string' ? existingEntry._id : existingEntry._id?.$oid || existingEntry._id?.toString();
              const existingTranslation = translationMap[`${entryIdStr}|en_US`];
              let entryUpdated = false;
              if (existingEntry.defaultContent.value !== value) {
                console.log('Updating existing Android entry:', key);
                const updatedEntry = {
                  ...existingEntry,
                  defaultContent: {
                    value
                  }
                };
                const res = await fetch(`${API_BASE_URL}/localization/${existingEntry._id}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(updatedEntry)
                });
                if (!res.ok) {
                  const errorData = await res.json();
                  throw new Error(`Failed to update entry for key: ${key}. API Error: ${JSON.stringify(errorData)}`);
                }
                console.log('Updated entry:', key);
                importProgress.updated++;
                entryUpdated = true;
                // Also update translation if it exists and value is different
                if (existingTranslation && existingTranslation.content.value !== value) {
                  const updateTranslationRes = await fetch(`${API_BASE_URL}/translations/${existingTranslation._id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      ...existingTranslation,
                      content: { value }
                    })
                  });
                  if (!updateTranslationRes.ok) {
                    const errorData = await updateTranslationRes.json();
                    throw new Error(`Failed to update translation for key: ${key}. API Error: ${JSON.stringify(errorData)}`);
                  }
                  console.log('Updated translation for:', key);
                  importProgress.updated++;
                }
              }
              if (!existingTranslation) {
                // Create new translation
                const newTranslation = {
                  entryId: entryIdStr,
                  languageCode: 'en_US',
                  content: { value },
                  version: 1
                };
                const createTranslationRes = await fetch(`${API_BASE_URL}/translations`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(newTranslation)
                });
                if (!createTranslationRes.ok) {
                  const errorData = await createTranslationRes.json();
                  throw new Error(`Failed to create translation for key: ${key}. API Error: ${JSON.stringify(errorData)}`);
                }
                console.log('Created translation for:', key);
                importProgress.created++;
                translationMap[`${entryIdStr}|en_US`] = newTranslation;
              }
              if (existingEntry.defaultContent.value === value && existingTranslation) {
                importProgress.skipped++;
                console.log('Skipping unchanged entry and translation:', key);
              }
            } else {
              // Create new entry for Android platform
              console.log('Creating new Android entry:', key);
              const newEntry = {
                key,
                type: 'String',
                platforms: ['Android'],
                defaultLanguageCode: 'en_US',
                defaultContent: {
                  value
                }
              };
              const res = await fetch(`${API_BASE_URL}/localization`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newEntry)
              });
              if (!res.ok) {
                const errorData = await res.json();
                throw new Error(`Failed to create entry for key: ${key}. API Error: ${JSON.stringify(errorData)}`);
              }
              const createdEntryResponse = await res.json();
              // Use entryId or _id from response
              const entryId = createdEntryResponse.entryId || createdEntryResponse._id;
              console.log('Created entry:', createdEntryResponse);
              importProgress.created++;
              // Add to in-memory map so subsequent lines see it
              entryMap[entryKey] = { ...newEntry, _id: entryId, platforms: ['Android'] };
              
              // In-memory translation check
              const entryIdStr = typeof entryId === 'string' ? entryId : entryId?.$oid || entryId?.toString();
              const existingTranslation = translationMap[`${entryIdStr}|en_US`];
              if (!existingTranslation) {
                // Create new translation
                const newTranslation = {
                  entryId: entryIdStr,
                  languageCode: 'en_US',
                  content: { value },
                  version: 1
                };
                const createTranslationRes = await fetch(`${API_BASE_URL}/translations`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(newTranslation)
                });
                if (!createTranslationRes.ok) {
                  const errorData = await createTranslationRes.json();
                  throw new Error(`Failed to create translation for key: ${key}. API Error: ${JSON.stringify(errorData)}`);
                }
                console.log('Created translation for:', key);
                importProgress.created++;
                // Add to in-memory map so subsequent lines see it
                translationMap[`${entryIdStr}|en_US`] = newTranslation;
              } else {
                console.log('Skipping unchanged entry and translation:', key);
                importProgress.skipped++;
              }
            }
          } else {
            // For non-default languages, use in-memory map
            const entryKey = `${key}|Android`;
            const existingEntry = entryMap[entryKey];
            if (existingEntry) {
              const entryIdStr = typeof existingEntry._id === 'string' ? existingEntry._id : existingEntry._id?.$oid || existingEntry._id?.toString();
              const existingTranslation = translationMap[`${entryIdStr}|${selectedLanguage}`];

              if (existingTranslation) {
                // Update if content has changed
                if (existingTranslation.content.value !== value) {
                  console.log('Updating translation for Android entry:', key);
                  const updateTranslationRes = await fetch(`${API_BASE_URL}/translations/${existingTranslation._id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      ...existingTranslation,
                      content: { value }
                    })
                  });
                  if (!updateTranslationRes.ok) {
                    const errorData = await updateTranslationRes.json();
                    throw new Error(`Failed to update translation for key: ${key}. API Error: ${JSON.stringify(errorData)}`);
                  }
                  console.log('Updated translation for:', key);
                  importProgress.updated++;
                } else {
                  console.log('Skipping unchanged translation:', key);
                  importProgress.skipped++;
                }
              } else {
                // Create new translation
                console.log('Creating new translation for Android entry:', key);
                const translation = {
                  entryId: entryIdStr,
                  languageCode: selectedLanguage,
                  content: {
                    value
                  },
                  version: 1
                };
                const res = await fetch(`${API_BASE_URL}/translations`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(translation)
                });
                if (!res.ok) {
                  const errorData = await res.json();
                  throw new Error(`Failed to create translation for key: ${key}. API Error: ${JSON.stringify(errorData)}`);
                }
                console.log('Created new translation for:', key);
                importProgress.created++;
              }
            } else {
              console.warn('Skipping translation for non-existent Android entry:', key);
              importProgress.skipped++;
            }
          }
          importProgress.processed++;
        } catch (error) {
          console.error(`Error processing key ${key}:`, error);
          importProgress.failed++;
        }
      }

      // Refresh the list
      await fetchEntries(currentPage);
      console.log('Import completed successfully');
    } catch (error) {
      console.error('Error importing file:', error);
      alert('Error importing file: ' + error.message);
    } finally {
      showImportProgress = false;
    }

    // Reset the file input
    event.target.value = '';
  }

  async function handleExportAndroidStrings() {
    try {
      // Get all entries for Android platform
      const res = await fetch(
        `${API_BASE_URL}/localization?platform=Android&type=String&pageSize=999`
      );
      if (!res.ok) throw new Error('Failed to fetch entries');
      const data = await res.json();
      const entries = data.data || [];

      // Get translations for selected language
      const tRes = await fetch(
        `${API_BASE_URL}/translations?languageCode=${selectedLanguage}`
      );
      if (!tRes.ok) throw new Error('Failed to fetch translations');
      const tData = await tRes.json();
      const translations = tData.data || [];

      // Create the JSON content
      const jsonContent = {};
      for (const entry of entries) {
        const translation = translations.find(t => t.entryId === entry._id);
        const value = translation?.content?.value || entry.defaultContent.value;
        jsonContent[entry.key] = value;
      }

      // Create and download the file
      const blob = new Blob([JSON.stringify(jsonContent, null, 2)], { type: 'application/json' });
      const language = languages.find(l => l.code === selectedLanguage);
      const fileName = `${language?.code || selectedLanguage}.json`;
      saveAs(blob, fileName);
    } catch (error) {
      console.error('Error exporting file:', error);
      alert('Error exporting file: ' + error.message);
    }
  }

  async function handleExportMessages() {
    try {
      // Get all entries for Messaging platform
      const res = await fetch(
        `${API_BASE_URL}/localization?platform=Messaging&type=Message&pageSize=999`
      );
      if (!res.ok) throw new Error('Failed to fetch entries');
      const data = await res.json();
      const entries = data.data || [];

      // Get translations for all languages
      const allTranslations = [];
      for (const language of languages) {
        const tRes = await fetch(
          `${API_BASE_URL}/translations?languageCode=${language.code}`
        );
        if (tRes.ok) {
          const tData = await tRes.json();
          allTranslations.push(...(tData.data || []));
        }
      }

      // Create the Excel content
      const formattedData = [];
      for (const entry of entries) {
        // Always include en_US
        formattedData.push({
          RuleId: entry.ruleId || '',
          LanguageCode: 'en_US',
          Title: entry.defaultContent.title || '',
          Message: entry.defaultContent.message || ''
        });

        // Find all translations for this entry
        const entryTranslations = allTranslations.filter(t => t.entryId === entry._id);
        
        // Add translations for other languages
        for (const translation of entryTranslations) {
          if (translation.languageCode !== 'en_US') {
            formattedData.push({
              RuleId: entry.ruleId || '',
              LanguageCode: translation.languageCode,
              Title: translation.content?.title || '',
              Message: translation.content?.message || ''
            });
          }
        }
      }

      // Sort the data:
      // 1. First by RuleId
      // 2. Then by languageCode (en_US first, then others alphabetically)
      formattedData.sort((a, b) => {
        // First sort by RuleId
        const ruleIdA = String(a.RuleId || '');
        const ruleIdB = String(b.RuleId || '');
        const ruleIdCompare = ruleIdA.localeCompare(ruleIdB);
        if (ruleIdCompare !== 0) return ruleIdCompare;

        // Then sort by languageCode
        if (a.LanguageCode === 'en_US') return -1;
        if (b.LanguageCode === 'en_US') return 1;
        return a.LanguageCode.localeCompare(b.LanguageCode);
      });

      // Create and download the file
      const now = new Date();
      const dateStr = now
        .toLocaleString('en-US', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        })
        .replace(/,/g, '')
        .replace(/\s+/g, '')
        .replace(/:/g, '')
        .replace(/(\d{2})([A-Za-z]{3})(\d{4})_(\d{2})(\d{2})/, '$2$1$3_$4$5');

      const filename = `Messages_Export_${dateStr}`;
      exportToExcel(formattedData, filename);
    } catch (error) {
      console.error('Error exporting messages:', error);
      alert('Error exporting messages: ' + error.message);
    }
  }
</script>

<StudyHeader />

<FeatureInfo
  title="Localization Management"
  description="This page allows you to manage all localization entries in the system. You can search, create, edit, and delete entries. Each entry includes details such as key, type, platforms, and content in different languages."
  marginBottom="mb-0"
/>

<Toolbar title="Localizations" itemCountTitle="Entries" itemCount={resultCount}>
  <div class="flex items-center gap-4">
    <select
      bind:value={selectedLanguage}
      on:change={handleLanguageChange}
      class="h-6 px-2 w-32 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100"
    >
      {#each languages as language}
        <option value={language.code}>{language.displayBase}</option>
      {/each}
    </select>
    <div class="relative">
      <input
        type="text"
        placeholder="Search"
        bind:value={searchText}
        on:input={() => fetchEntries(1)}
        class="px-3 py-1.5 text-xs text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 pr-8"
      />
      {#if searchText}
        <button
          on:click={handleClearSearch}
          class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          title="Clear search"
        >
          <FontAwesomeIcon icon={faCircleXmark} />
        </button>
      {/if}
    </div>
    <select
      bind:value={selectedType}
      on:change={() => fetchEntries(1)}
      class="h-6 px-2 w-32 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100"
    >
      {#each typeOptions as type}
        <option value={type}>{type}</option>
      {/each}
    </select>
    <select
      bind:value={selectedPlatform}
      on:change={() => fetchEntries(1)}
      class="h-6 px-2 w-32 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100"
    >
      {#each availablePlatformOptions as platform}
        <option value={platform}>{platform}</option>
      {/each}
    </select>
    <button
      on:click={openCreateModal}
      class="pagination mx-0 p-0"
      disabled={!canAddEntries}
      class:opacity-50={!canAddEntries}
      title={!canAddEntries ? 'Insufficient permissions' : 'Add new entry'}
    >
      <FontAwesomeIcon icon={faSquarePlus} />
    </button>
    <button
      on:click={handleRefresh}
      class="pagination mx-0 p-0"
      disabled={isLoading}
      title="Refresh"
    >
      <FontAwesomeIcon icon={faRotate} spin={isLoading} />
    </button>
  </div>
</Toolbar>

<div class="px-1 pt-0">
  <table class="min-w-full border-collapse table-auto">
    <thead>
      <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
        <th class="px-4 py-2 text-left bold cursor-pointer" on:click={() => handleSort('key')}>
          Key {sortField === 'key' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
        </th>
        <th class="px-4 py-2 text-left bold">Default (en_US)</th>
        <th class="px-4 py-2 text-left bold">Translation ({selectedLanguage})</th>
        <th class="w-px px-4 py-2 text-left bold">Actions</th>
      </tr>
    </thead>
    <tbody>
      {#if isTableLoading}
        <tr>
          <td colspan="5" class="text-center text-gray-500 dark:text-slate-400 mt-10 py-10">
            Loading translations...
          </td>
        </tr>
      {:else}
        {#each entries as entry, index}
          <tr
            class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
              2 ===
            0
              ? 'bg-stone-50 dark:bg-gray-725'
              : 'bg-stone-75 dark:bg-gray-750'}"
          >
            <td class="px-4 py-2">{entry.type === 'Message' ? entry.key : entry.key}</td>
            <td class="px-4 py-2">{getContentPreview(entry)}</td>
            <td class="px-4 py-2">
              {#if editingTranslation?._id === entry._id}
                <div class="flex items-center gap-2 w-full">
                  {#if entry.type === 'String'}
                    <input
                      type="text"
                      bind:value={editingContent.value}
                      class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                      placeholder="Enter translation"
                      on:blur={saveInlineEdit}
                    />
                  {:else if entry.type === 'Message'}
                    <input
                      type="text"
                      bind:value={editingContent.title}
                      class="border p-1 w-1/3 h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                      placeholder="Enter title"
                    />
                    <input
                      type="text"
                      bind:value={editingContent.message}
                      class="border p-1 w-2/3 h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                      placeholder="Enter message"
                      on:blur={saveInlineEdit}
                    />
                  {/if}
                  <button
                    class="ml-1 p-0 bg-transparent border-0 text-gray-500 hover:text-purple-600 focus:outline-none"
                    title="AI generate translation"
                    type="button"
                    on:click={() => generateAITranslation(entry, selectedLanguage)}
                    disabled={isAIGenerating}
                  >
                    {#if isAIGenerating}
                      <svg
                        class="animate-spin h-5 w-5 text-purple-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        ><circle
                          class="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          stroke-width="4"
                        ></circle><path
                          class="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8z"
                        ></path></svg
                      >
                    {:else}
                      <FontAwesomeIcon icon={faWandMagicSparkles} />
                    {/if}
                  </button>
                  <button
                    class="ml-1 p-0 bg-transparent border-0 text-gray-500 hover:text-red-500 focus:outline-none"
                    title="Cancel"
                    on:click={cancelInlineEdit}
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                  <button
                    class="ml-1 p-0 bg-transparent border-0 text-gray-500 hover:text-green-600 focus:outline-none"
                    title="Save"
                    on:click={saveInlineEdit}
                  >
                    <FontAwesomeIcon icon={faCheck} />
                  </button>
                </div>
              {:else if getTranslationForEntry(entry._id)}
                <div class="group relative">
                  {#if entry.type === 'String'}
                    <!-- svelte-ignore a11y-click-events-have-key-events -->
                    <!-- svelte-ignore a11y-no-static-element-interactions -->
                    <span
                      class="cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                      on:click={() => startInlineEdit(entry)}
                    >
                      {getTranslationForEntry(entry._id).content.value}
                    </span>
                  {:else if entry.type === 'Message'}
                    <!-- svelte-ignore a11y-click-events-have-key-events -->
                    <!-- svelte-ignore a11y-no-static-element-interactions -->
                    <span
                      class="cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                      on:click={() => startInlineEdit(entry)}
                    >
                      {getTranslationForEntry(entry._id).content.title}: {getTranslationForEntry(
                        entry._id
                      ).content.message}
                    </span>
                  {/if}
                  {#if canEditEntries}
                    <button
                      class="ml-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 text-lg p-0 border-0 bg-transparent align-middle opacity-0 group-hover:opacity-100 transition-opacity"
                      style="line-height:1;"
                      on:click={() => startInlineEdit(entry)}
                      title="Edit translation"
                    >
                      <FontAwesomeIcon icon={faPencil} class="w-4 h-4" />
                    </button>
                  {/if}
                </div>
              {:else}
                <span class="text-gray-400 italic">No translation</span>
                {#if canEditEntries}
                  <button
                    class="ml-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 text-lg p-0 border-0 bg-transparent align-middle"
                    style="line-height:1;"
                    on:click={() => startInlineEdit(entry)}
                    title="Add translation"
                  >
                    <FontAwesomeIcon icon={faPencil} class="w-4 h-4" />
                  </button>
                {/if}
              {/if}
            </td>
            <td class="w-px px-4 py-2">
              <div class="flex items-center space-x-2">
                <button
                  on:click={() => openEditModal(entry)}
                  class="pagination mx-1 p-0"
                  title="Edit"
                  disabled={!canEditEntries}
                  class:opacity-50={!canEditEntries}
                >
                  <FontAwesomeIcon icon={faPencil} class="text-sky-800 dark:text-white/70" />
                </button>
                <button
                  on:click={() => handleAuditLogClick(entry)}
                  class="pagination mx-1 p-0"
                  title="View audit logs"
                >
                  <FontAwesomeIcon icon={faHistory} class="text-sky-800 dark:text-white/70" />
                </button>
                <button
                  on:click={() => openDeleteModal(entry)}
                  class="pagination mx-1 p-0"
                  title="Delete"
                  disabled={!canDeleteEntries}
                  class:opacity-50={!canDeleteEntries}
                >
                  <FontAwesomeIcon icon={faTrashCan} class="text-sky-800 dark:text-white/70" />
                </button>
              </div>
            </td>
          </tr>
        {/each}
        {#if entries.length === 0}
          <tr>
            <td colspan="5" class="text-center text-gray-500 dark:text-slate-400 mt-10 py-10">
              No entries found
            </td>
          </tr>
        {/if}
      {/if}
    </tbody>
  </table>
</div>

<Footer>
  <div class="flex items-center justify-between w-full">
    <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
    {#if selectedType === 'String' && selectedPlatform === 'iOS'}
      <div class="flex items-center gap-2">
        <input
          type="file"
          accept=".strings"
          class="hidden"
          on:change={handleImportIOSStrings}
          id="import-ios-strings"
        />
        <button
          class="px-3 py-1 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600"
          on:click={() => document.getElementById('import-ios-strings').click()}
        >
          Import iOS Strings
        </button>
        <button
          class="px-3 py-1 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600"
          on:click={handleExportIOSStrings}
        >
          Export iOS Strings
        </button>
      </div>
    {:else if selectedType === 'String' && selectedPlatform === 'Android'}
      <div class="flex items-center gap-2">
        <input
          type="file"
          accept=".json"
          class="hidden"
          on:change={handleImportAndroidStrings}
          id="import-android-strings"
        />
        <button
          class="px-3 py-1 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600"
          on:click={() => document.getElementById('import-android-strings').click()}
        >
          Import Android Strings
        </button>
        <button
          class="px-3 py-1 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600"
          on:click={handleExportAndroidStrings}
        >
          Export Android Strings
        </button>
      </div>
    {:else if selectedType === 'Message'}
      <div class="flex items-center gap-2">
        <button
          class="px-3 py-1 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600"
          on:click={handleExportMessages}
        >
          Export Messages
        </button>
      </div>
    {/if}
  </div>
</Footer>

{#if showCreateModal}
  <LocalizationEntryModal
    entry={selectedEntry}
    onClose={() => (showCreateModal = false)}
    onSave={handleCreateEntry}
    mode="create"
  />
{/if}

{#if showEditModal}
  <LocalizationEntryModal
    entry={selectedEntry}
    onClose={() => (showEditModal = false)}
    onSave={handleUpdateEntry}
    mode="edit"
  />
{/if}

{#if showDeleteModal}
  <ConfirmationModal
    showConfirmation={true}
    message="Are you sure you want to delete this localization entry? This action cannot be undone."
    on:confirm={handleDeleteEntry}
    on:close={() => (showDeleteModal = false)}
  />
{/if}

{#if showAuditLog}
  <AuditLogModal
    showModal={showAuditLog}
    documentId={auditLogEntry?._id}
    collection="Localization"
    title={`Audit Logs - ${auditLogEntry?.key || 'Localization Entry'}`}
    on:close={() => (showAuditLog = false)}
  />
{/if}

{#if showTranslationModal}
  <div class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
      class="modal-overlay absolute inset-0 bg-slate-800 opacity-75"
      on:click={() => (showTranslationModal = false)}
    ></div>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
      class="bg-white dark:bg-gray-900 p-4 rounded-lg shadow-lg w-full max-w-2xl relative z-60 overflow-y-auto max-h-[90vh]"
      on:click|stopPropagation
    >
      <header class="flex justify-between items-start mb-4 text-gray-900 dark:text-gray-100">
        <h2 class="text-xl font-bold">
          {translationModalType === 'edit' ? 'Edit' : 'Add'} Translation ({translationModalEntry?.key})
        </h2>
        <button
          class="text-2xl text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
          on:click={() => (showTranslationModal = false)}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </header>
      <form on:submit|preventDefault={handleSaveTranslation} class="space-y-4">
        {#if translationModalEntry?.type === 'String'}
          <div>
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >Value</label
            >
            <input
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              bind:value={translationModalContent.value}
              placeholder="Enter text content"
            />
          </div>
        {:else if translationModalEntry?.type === 'Message'}
          <!-- svelte-ignore a11y-label-has-associated-control -->
          <div>
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >Title</label
            >
            <input
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              bind:value={translationModalContent.title}
              placeholder="Enter message title"
            />
          </div>
          <div>
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >Message</label
            >
            <textarea
              class="border p-1 w-full rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              bind:value={translationModalContent.message}
              placeholder="Enter message content"
              rows="3"
            ></textarea>
          </div>
        {/if}
        <div class="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            on:click={() => (showTranslationModal = false)}
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
            >Cancel</button
          >
          <button
            type="submit"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600"
            >Save</button
          >
        </div>
      </form>
    </div>
  </div>
{/if}

<!-- Add progress modal -->
{#if showImportProgress}
  <div class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
    <div class="bg-white dark:bg-gray-900 p-6 rounded-lg shadow-lg w-full max-w-md">
      <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Importing Localizations</h3>
      <div class="space-y-4">
        <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
          <div
            class="bg-blue-600 h-2.5 rounded-full"
            style="width: {Math.round((importProgress.processed / importProgress.total) * 100)}%"
          ></div>
        </div>
        <div class="text-sm text-gray-600 dark:text-gray-400">
          Processing {importProgress.processed} of {importProgress.total} entries
        </div>
        <div class="text-sm text-gray-600 dark:text-gray-400-1">
          <div>Created: {importProgress.created}</div>
          <div>Updated: {importProgress.updated}</div>
          <div>Skipped: {importProgress.skipped}</div>
          <div>Failed: {importProgress.failed}</div>
        </div>
      </div>
    </div>
  </div>
{/if}
